import moment from "moment";
import { useShowContext, useRefresh } from "react-admin";
import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Box,
  Typography,
  Chip,
  Link,
} from "@mui/material";

//Common Styles
import {
  CustomTableRowTR,
  EmptyView,
  OutlinedButton,
  Paragraph,
  StatusBadge,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
  WorkspacePanel,
} from "../../styles/commonStyles";

//Events
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import EventBus from "../../EventBus";

//Components
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import AddDishDialog from "./AddDishDialog";

//Services
import ChefForPartyService from "../../services/ChefForPartyService";

//Icons
import { EditIcon } from "../Svg";
import { DeleteIcon } from "../Svg/DeleteIcon";

//Colors
import { colors } from "../../styles/colors";

//Enums
import { ChefForPartyBookingTypeEnum } from "../../enums/ChefForPartyBookingTypeEnum";
import { lineQuantityText } from "../../utils/BookByDishPlate";

const mealVariantMap: any = {
  STARTER: "starter",
  "MAIN COURSE": "mainCourse",
  DESSERT: "dessert",
  SIDE_DISH: "sideDish",
  BEVERAGE: "beverage",
};

const mealTypeOrder: { [key: string]: number } = {
  STARTER: 0,
  "MAIN COURSE": 1,
  DESSERT: 2,
  SIDE_DISH: 3,
  BEVERAGE: 4,
};

const sortByMealType = (a: any, b: any) =>
  (mealTypeOrder[a?.mealType] ?? 99) - (mealTypeOrder[b?.mealType] ?? 99);

const mealTypeTitles: { [key: string]: string } = {
  STARTER: "Starters",
  "MAIN COURSE": "Main Course",
  DESSERT: "Desserts",
  SIDE_DISH: "Side Dishes",
  BEVERAGE: "Beverages",
};

const chefForPartyService = new ChefForPartyService();

const ChefForPartyDishesList = ({ record }: any) => {
  const { record: showRecord } = useShowContext();
  const refresh = useRefresh();

  const isBookByDish = showRecord?.bookingType === ChefForPartyBookingTypeEnum.BOOK_BY_DISH;

  const dishes = useMemo(() => record?.dishes || [], [record?.dishes]);

  const customerDishes = useMemo(
    () =>
      dishes
        .filter((dish: any) => dish.createdByType !== "COOK")
        .sort(sortByMealType),
    [dishes],
  );

  const cookDishes = useMemo(
    () => dishes.filter((dish: any) => !dish.deletedAt).sort(sortByMealType),
    [dishes],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_loading, setLoading] = useState<boolean>(false);

  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [selectedMealType, setSelectedMealType] = useState<string>("");
  const [editingDish, setEditingDish] = useState<any>(null);

  const handleDishAddClose = () => {
    setOpenAdd(false);
    setEditingDish(null);
  };

  const handleDishAddOpen = () => {
    setOpenAdd(true);
  };

  const handleDishEditOpen = (dish: any) => {
    setEditingDish(dish);
    setSelectedMealType(dish.mealType);
    setOpenAdd(true);
  };

  const formatDishesForCopy = (dishes: any[] = []) => {
    return dishes
      .sort((a, b) => a?.mealType?.localeCompare(b?.mealType))
      .map((dish, index) =>
        index !== dishes.length - 1
          ? `${dish?.dishName},`
          : `${dish?.dishName}.`,
      )
      .join("\n");
  };

  const copyDishes = () => {
    const formatted = formatDishesForCopy(dishes.filter((dish: any) => dish?.deletedAt === null));

    navigator.clipboard
      .writeText(formatted)
      .then(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Dish list copied!",
          type: CustomSnackbarTypes.SUCCESS,
        });
      })
      .catch(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Failed to copy",
          type: CustomSnackbarTypes.ERROR,
        });
      });
  };

  const removeDish = useCallback(
    (dishToDelete: any) => {
      EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
        message: "Are you sure you want to remove this dish?",
        onClose: (confirmed: boolean) => {
          if (confirmed) {
            const updatedDishes = dishes.filter(
              (dish: any) => dish !== dishToDelete,
            );

            setLoading(true);
            chefForPartyService
              .addDIshChefForParty(Number(showRecord?.id), {
                dishes: updatedDishes,
              })
              .then(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                  message: "Dish removed successfully!",
                  type: CustomSnackbarTypes.SUCCESS,
                });
                refresh();
              })
              .catch((error) => {
                console.error("Error removing dish:", error);
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                  message: "Failed to remove dish",
                  type: CustomSnackbarTypes.ERROR,
                });
              })
              .finally(() => {
                setLoading(false);
              });
          }
        },
        title: "Confirmation",
      });
    },
    [dishes, showRecord?.id, refresh],
  );

  const groupedDishes = useMemo(() => {
    return dishes
      .filter((dish: any) => !dish.deletedAt)
      .reduce((acc: any, dish: any) => {
        const mealType = dish?.mealType;
        if (!acc[mealType]) {
          acc[mealType] = [];
        }
        acc[mealType].push(dish);
        return acc;
      }, {});
  }, [dishes]);

  const renderDishGroup = useCallback(
    (mealType: string, dishes: any[]) => {
      const mealTypeTitle = mealTypeTitles[mealType] || mealType;
      return (
        <Box key={mealType} sx={{ marginBottom: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Title>{mealTypeTitle}</Title>
            {!isBookByDish && (
              <OutlinedButton
                style={{ fontSize: "12px" }}
                onClick={() => {
                  setSelectedMealType(mealType);
                  handleDishAddOpen();
                }}
              >
                Add Dish
              </OutlinedButton>
            )}
          </Box>
          {dishes.length > 0 ? (
            <Grid container spacing={2}>
              {dishes.map((dish: any, index: number) => (
                <Grid item xs={12} sm={6} md={6} key={index}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      border: "1px solid rgb(226, 230, 233)",
                    }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Paragraph>{dish.dishName}</Paragraph>
                      <StatusBadge
                        variant={dish?.type === "VEG" ? "veg" : "nonVeg"}
                      >
                        {dish?.type}
                      </StatusBadge>
                      <Chip
                        label={mealTypeTitle}
                        size="small"
                        sx={{
                          backgroundColor: "#ffe0b2",
                          color: "#e65100",
                        }}
                      />
                    </Box>
                    {!isBookByDish && (
                      <Box
                        sx={{ display: "flex", flexDirection: "row", gap: 0.5 }}
                      >
                        <IconButton
                          size="small"
                          sx={{ padding: 0.5 }}
                          onClick={() => handleDishEditOpen(dish)}
                        >
                          <EditIcon color={colors.primaryDark} />
                        </IconButton>
                        <IconButton
                          onClick={() => removeDish(dish)}
                          size="small"
                          color="error"
                          sx={{ padding: 0.5 }}
                        >
                          <DeleteIcon color={"red"} width={20} height={20} />
                        </IconButton>
                      </Box>
                    )}
                  </div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <EmptyView>
              <Typography
                variant="body1"
                sx={{ color: "#666", marginBottom: 1 }}
              >
                No items added in {mealTypeTitle}
              </Typography>
            </EmptyView>
          )}
        </Box>
      );
    },
    [removeDish, isBookByDish],
  );

  return (
    <>
      <TableWrapper>
        <TableTopHeader>
          <Title>Menu Items</Title>
          <Button size="small" variant="outlined" onClick={copyDishes}>
            Copy
          </Button>
        </TableTopHeader>

        {/* --- Customer Added --- */}
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", letterSpacing: "0.4px", mt: "12px", mb: "10px", px: { xs: "0px", sm: "24px" } }}>
          By Customer
        </Typography>
        <Box sx={{ px: { xs: "0px", sm: "24px" }, pb: "8px" }}>
          {customerDishes.length === 0 ? (
            <Typography sx={{ padding: "12px 0", color: "#6C7C93", fontSize: "14px" }}>
              No items added by customer
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: "8px", mb: "20px" }}>
              <StyledTable style={{ tableLayout: "auto", width: "100%" }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                    <TableHeader style={{ whiteSpace: "nowrap" }}>#</TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Type</TableHeader>
                    <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Meal Type</TableHeader>
                    {isBookByDish && <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Count</TableHeader>}
                    {isBookByDish && <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Cost</TableHeader>}
                    {isBookByDish && <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Time (min)</TableHeader>}
                    <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Updated At</TableHeader>
                    <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Deleted At</TableHeader>
                    <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Deleted By Type</TableHeader>
                    <TableHeader style={{ textAlign: "center", whiteSpace: "nowrap" }}>Deleted By</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerDishes.map((row: any, index: number) => (
                    <CustomTableRowTR
                      key={row?.id ?? index}
                      sx={row?.deletedAt ? {
                        "--row-bg": "rgba(239, 68, 68, 0.12) !important",
                        "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.12) !important" },
                        "&:active": { backgroundColor: "rgba(239, 68, 68, 0.12) !important" },
                      } as any : {}}
                    >
                      <TableData style={{ whiteSpace: "nowrap" }}>{index + 1}</TableData>
                      <TableData>{row?.dishName || "-"}</TableData>
                      <TableData style={{ textAlign: "center" }}>
                        {row ? (
                          <StatusBadge variant={row?.type === "VEG" ? "veg" : "nonVeg"}>
                            {row?.type}
                          </StatusBadge>
                        ) : "-"}
                      </TableData>
                      <TableData style={{ textAlign: "center" }}>
                        {row ? (
                          <StatusBadge variant={mealVariantMap[row?.mealType]}>
                            {row?.mealType}
                          </StatusBadge>
                        ) : "-"}
                      </TableData>
                      {isBookByDish && (
                        <TableData style={{ textAlign: "center" }}>
                          {row ? lineQuantityText(row, { dishType: row?.dish?.dishType ?? row?.dishType, plateUnit: row?.dish?.plateUnit }) : "-"}
                        </TableData>
                      )}
                      {isBookByDish && (
                        <TableData style={{ textAlign: "center" }}>
                          {row?.price != null ? `₹${row.price}` : "-"}
                        </TableData>
                      )}
                      {isBookByDish && (
                        <TableData style={{ textAlign: "center" }}>{row?.time ?? "-"}</TableData>
                      )}
                      <TableData style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {row?.updatedAt ? moment(row.updatedAt).format("DD MMM YYYY hh:mm A") : "-"}
                      </TableData>
                      <TableData style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {row?.deletedAt ? moment(row.deletedAt).format("DD MMM YYYY hh:mm:ss A") : "-"}
                      </TableData>
                      <TableData style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {row?.deletedByType || "-"}
                      </TableData>
                      <TableData style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {row?.deletedBy ? (
                          <Link
                            href={`/#/${row.deletedByType === "CUSTOMER" ? "Customers" : "Cooks"}/${row.deletedBy}/show`}
                            underline="hover"
                            sx={{ cursor: "pointer" }}
                          >
                            {row.deletedBy}
                          </Link>
                        ) : "-"}
                      </TableData>
                    </CustomTableRowTR>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          )}
        </Box>

        {/* --- Cook Added --- */}
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", letterSpacing: "0.4px", mt: "24px", mb: "10px", px: { xs: "0px", sm: "24px" } }}>
          By Cook
        </Typography>
        <Box sx={{ px: { xs: "0px", sm: "24px" }, pb: "16px" }}>
          {cookDishes.length === 0 ? (
            <Typography sx={{ padding: "12px 0", color: "#6C7C93", fontSize: "14px" }}>
              No items added by cook
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
              {/* Auto layout so plate counts ("2 Plates · 16 pcs") size their own column. */}
              <StyledTable style={{ tableLayout: "auto", width: "100%", minWidth: isBookByDish ? 920 : 640 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                    <TableHeader>#</TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>Type</TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>Meal Type</TableHeader>
                    {isBookByDish && <TableHeader style={{ textAlign: "center" }}>Count</TableHeader>}
                    {isBookByDish && <TableHeader style={{ textAlign: "center" }}>Cost</TableHeader>}
                    {isBookByDish && <TableHeader style={{ textAlign: "center" }}>Time (min)</TableHeader>}
                    <TableHeader style={{ textAlign: "center", paddingLeft: "28px" }}>Updated At</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cookDishes.map((row: any, index: number) => (
                    <CustomTableRowTR key={row?.id}>
                      <TableData>{index + 1}</TableData>
                      <TableData style={{ whiteSpace: "normal", minWidth: "160px" }}>
                        {row?.dishName || "-"}
                      </TableData>
                      <TableData style={{ textAlign: "center" }}>
                        <StatusBadge variant={row?.type === "VEG" ? "veg" : "nonVeg"}>
                          {row?.type}
                        </StatusBadge>
                      </TableData>
                      <TableData style={{ textAlign: "center" }}>
                        <StatusBadge variant={mealVariantMap[row?.mealType]}>
                          {row?.mealType}
                        </StatusBadge>
                      </TableData>
                      {isBookByDish && (
                        <TableData style={{ textAlign: "center" }}>
                          {lineQuantityText(row, { dishType: row?.dish?.dishType ?? row?.dishType, plateUnit: row?.dish?.plateUnit })}
                        </TableData>
                      )}
                      {isBookByDish && (
                        <TableData style={{ textAlign: "center" }}>
                          {row?.price != null ? `₹${row.price}` : "-"}
                        </TableData>
                      )}
                      {isBookByDish && (
                        <TableData style={{ textAlign: "center" }}>{row?.time ?? "-"}</TableData>
                      )}
                      <TableData style={{ textAlign: "center", paddingLeft: "28px" }}>
                        {row?.updatedAt ? moment(row.updatedAt).format("Do MMMM YYYY h:mm A") : "-"}
                      </TableData>
                    </CustomTableRowTR>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          )}
        </Box>
      </TableWrapper>

      <br />

      <WorkspacePanel>
        {Object.keys(mealTypeTitles).map((mealType) =>
          renderDishGroup(mealType, groupedDishes[mealType] || []),
        )}
      </WorkspacePanel>

      <AddDishDialog
        open={openAdd}
        mealType={selectedMealType}
        existingDishes={dishes.filter((dish: any) => !dish.deletedAt)}
        partyId={showRecord?.id}
        editingDish={editingDish}
        onClose={handleDishAddClose}
        onSuccess={() => refresh()}
      />
    </>
  );
};

export default ChefForPartyDishesList;
